import { Component, ElementRef, HostBinding, HostListener, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ADMIN_TABLE, AdminSortOption, AdminTableSortHost } from './admin-table.token';
import { AdminTableIconComponent } from './table-icon.component';

/**
 * `<th adminSort="field">Label</th>` inside an admin-table header template.
 * (`&ngsp;` keeps the space between label and icon that PrimeNG's markup had.)
 * The label is a button, so Tab, Enter and Space all work; its click bubbles
 * to the header cell, which sorts by the field, toggling ascending/descending.
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector -- used as an attribute on <th>
  selector: 'th[adminSort]',
  standalone: true,
  imports: [AdminTableIconComponent],
  template: `<button type="button" class="admin-sort-button">
    <ng-content></ng-content>&ngsp;<admin-table-icon
      class="admin-sort-icon"
      [name]="icon"
      aria-hidden="true"
    ></admin-table-icon>
  </button>`,
})
export class AdminSortHeaderComponent implements AdminSortOption, OnInit, OnDestroy {
  private readonly table: AdminTableSortHost = inject(ADMIN_TABLE);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input({ required: true }) adminSort!: string;

  get field(): string {
    return this.adminSort;
  }

  get element(): Element {
    return this.host.nativeElement;
  }

  get label(): string {
    return this.host.nativeElement.textContent?.trim() || this.adminSort;
  }

  ngOnInit(): void {
    this.table.registerSort(this);
  }

  ngOnDestroy(): void {
    this.table.unregisterSort(this);
  }

  @HostBinding('class.admin-sortable') readonly sortable = true;

  @HostBinding('class.admin-sorted')
  get sorted(): boolean {
    return this.table.sortField === this.adminSort;
  }

  @HostBinding('attr.aria-sort')
  get ariaSort(): string {
    return this.sorted ? (this.table.sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
  }

  get icon() {
    return !this.sorted ? 'sortAlt' : this.table.sortDir === 'asc' ? 'sortAmountUpAlt' : 'sortAmountDown';
  }

  @HostListener('click')
  toggle(): void {
    this.table.sortBy(this.adminSort);
  }
}
